import { create } from 'zustand';

import { getPlaybook } from './api-client';
import { isVersionConflictError } from './conflict-errors';
import { saveAndApplyToCampaigns, savePlaybook, savePlayLadder } from './mutations';
import {
  addDraftRung,
  assembleDraftRungs,
  rebaseDraft,
  remapDraftRungSegmentation,
  remapDraftRungSequence,
  removeDraftRung,
  resolveDraftKey,
  rungDraftKey,
  seedDraftFromHead,
  setDraftRungMapping,
} from './playbook-draft';
import type { DraftRung, NewDraftRungInput, PlaybookDraft, PlaybookDraftHead } from './playbook-draft';
import type { PlaybooksSavePlaybookRungsResponse, SequenceMapping } from './schemas';
import { migrateCopyDraftScope } from './sequence-copy-draft-store';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`), never imported here. */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * Launcher V2 — where the play flow's playbook ladder lives.
 *
 * 2026-09-02 rip-out (plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md,
 * U4 "write-through rung save") REPEALS this store's old job. It used to hold
 * a STAGED draft that only became real when Review's "Save Playbook" posted
 * the whole ladder at once (Decision 4). Every rung add/remove/mapping/sender
 * edit now round-trips through `POST /v2/playbooks/:id/save` the moment it
 * happens, via {@link persistLadder} — the ladder held here is always either
 * exactly what the server last confirmed, or an optimistic local apply of an
 * op whose save is still in flight. There is no more "Save Playbook" button
 * anywhere in the app; the plain "playbook edit" and "playbook save" are the
 * same user action now.
 *
 * Module-scope zustand (root CLAUDE.md "Zustand for Module-Scope Client State"),
 * modelled on `rung-scope-store.ts`, and for the same structural reason: the
 * play flow's steps are Glaze `TabPanel`s, and base-ui `Tabs` UNMOUNT inactive
 * panels. A draft held in `QualifyStep` state would be destroyed the moment the
 * rep tabbed to Sequences or Review. It also has to be readable from multiple
 * places at once: `QualifyStep` edits the ladder, `SequencesStep`'s mapping
 * drawer and inline sender popover edit it too, and `ReviewStep` reviews it —
 * all siblings.
 *
 * Actions are module-level functions rather than fields on the state object, the
 * same shape `rung-scope-store.ts` uses. That is what makes the documented reset
 * idiom `usePlaybookDraftStore.setState(initialState, true)` correct: `true`
 * REPLACES the state, so actions stored inside it would be wiped by the very
 * call tests use to reset.
 *
 * `PlayFlowShell` resets this store on mount and unmount, keyed on the Play, so
 * one Play's ladder can never be saved onto another Play's playbook.
 */
interface PlaybookDraftState {
  /**
   * Which playbook the draft belongs to — `null` when nothing is seeded.
   *
   * Held alongside the draft, not derived, because it is the guard that makes
   * {@link ensurePlaybookDraft} safe to call from a render-driven effect: it is
   * how "seed once for THIS playbook" is distinguished from "the rep opened a
   * different Play and the old draft must go", and it is the guard
   * {@link persistLadder} uses so a slow write from a Play the rep has since
   * navigated away from cannot land on a different Play's open ladder.
   */
  playbookId: string | null;
  /**
   * Plan 2026-09-25-002, U6 (Key Technical Decision 16) — the play whose
   * ladder this draft is, or `null` in the playbook doorway. When set, every
   * write goes to `POST /plays/:id/ladder` ({@link savePlayLadder}) instead of
   * the template's `POST /playbooks/:id/save`, and the 409 refetch reads the
   * play's ladder (`getPlaybook(…, { playId })`). Part of the draft's identity:
   * the same playbook opened in a different play (or in the doorway) re-seeds.
   */
  playId: string | null;
  /** `null` until seeded. A missing draft is never treated as an empty ladder. */
  draft: PlaybookDraft | null;
}

/** Exported per the store convention — reset idiom is `usePlaybookDraftStore.setState(initialState, true)`. */
export const initialState: PlaybookDraftState = {
  playbookId: null,
  playId: null,
  draft: null,
};

export const usePlaybookDraftStore = create<PlaybookDraftState>(() => ({ ...initialState }));

/**
 * Seeds a fresh draft for `playbookId` from its head, and does NOTHING if a
 * draft for that same playbook is already open.
 *
 * The no-op case is the whole point. `QualifyStep`/`SequencesStep`/`ReviewStep`
 * call this from an effect whose `head` comes from a react-query result, so it
 * re-runs on every refetch — including the ladder invalidation any
 * write-through save fires. Re-seeding there would race an optimistic local
 * apply {@link persistLadder} just made against a save that hasn't landed yet.
 */
export function ensurePlaybookDraft(
  playbookId: string,
  head: PlaybookDraftHead | null,
  /** Plan 002, U6 — the play the ladder belongs to; `null` (default) in the playbook doorway. */
  playId: string | null = null,
): void {
  const state = usePlaybookDraftStore.getState();
  if (state.playbookId === playbookId && state.playId === playId && state.draft !== null) return;
  usePlaybookDraftStore.setState({ playbookId, playId, draft: seedDraftFromHead(head) });
}

/**
 * Discards the whole draft — the reset `PlayFlowShell` runs on mount/unmount.
 *
 * Clears `playbookId` too: a stale playbook id with no draft would make the next
 * {@link ensurePlaybookDraft} for that same playbook look like a re-render and
 * skip the seed.
 */
export function clearPlaybookDraft(): void {
  usePlaybookDraftStore.setState({ ...initialState });
}

/**
 * Adopts a server response as the new truth for `playbookId`'s ladder — the
 * ONE place this store discards whatever it held locally, because there is
 * nothing left to preserve: the response IS what the client just asked the
 * server to persist (plus server-minted ids and a fresh `versionId`).
 *
 * Two callers: a successful write-through save (`persistLadder`, below) and a
 * successful waterfall REORDER (`QualifyStep#WaterfallCard`'s own
 * `reorderWaterfall` call, a sibling endpoint this store has no other opinion
 * about) — both hand this function `{ versionId, rungs }` fresh off the wire.
 *
 * ── MIGRATES `new:<n>`-SCOPED COPY DRAFTS (Codex adversarial-review fix,
 * 2026-09-02, Issue 1) ─────────────────────────────────────────────────────
 * A rung staged this session carries a `new:<n>` `draftKey`
 * (`playbook-draft.ts`'s `nextNewDraftKey`); any copy the rep typed on it
 * before it persisted lives in `sequence-copy-draft-store.ts` under that SAME
 * `new:<n>` scope. `nextNewDraftKey` computes the next key as one past the
 * highest `new:` key CURRENTLY IN THE DRAFT — so the instant a save removes
 * that rung from the draft's `new:` numbering (it is persisted now, addressed
 * by `rung:<id>`), the identical `new:<n>` key becomes available for reuse by
 * the next rung the rep adds. Left unmigrated, that reused key would still
 * point at the first rung's old draft — so a second, unrelated rung landing on
 * the same `new:<n>` key and mapping the same sequence would silently inherit
 * the first rung's stale draft — the exact D1 cross-rung leak, reached through
 * key reuse instead of missing scope.
 *
 * The fix relies on the fact that a successful save wrote EXACTLY what the OLD
 * draft staged, in the SAME order (`api.ts#assembleSavedRungs` maps the
 * submitted array 1:1, minting an id only where one was missing) — so
 * `head.rungs[i]` is the server's answer for `draft.rungs[i]`. Reading the OLD
 * draft (before it is replaced below) and zipping it against `head.rungs` by
 * index is therefore enough to know, for every rung that had no id, which
 * `rung:<id>` key its `new:<n>` draft now belongs under. `migrateCopyDraftScope`
 * moves it there. A rung that already had an id (nothing to migrate) is
 * skipped — and a REORDER response is a no-op through this same loop, since a
 * reorder can never submit an id-less rung.
 *
 * ── GUARDED BY THE CURRENTLY-OPEN PLAYBOOK (bug found verifying U4) ────────
 * Both callers are plain asyncs that can resolve AFTER the rep has navigated
 * to a different Play — `persistLadderOnce`'s own guard only checks
 * `state.playbookId` once, before its `await`, and `WaterfallCard`'s reorder
 * handler has no guard of its own at all. Unconditionally calling
 * `setState({ playbookId, draft: ... })` here would force the store BACK onto
 * this stale `playbookId`, clobbering whatever OTHER playbook's draft the rep
 * has since opened — the exact cross-Play draft-clobbering bug the old
 * (pre-rip-out) `rebasePlaybookDraft` guarded against. Re-checking
 * `state.playbookId === playbookId` here, immediately before adopting, closes
 * that gap for both callers at their one shared choke point instead of
 * duplicating the guard at each call site.
 *
 * ── ALSO GUARDED BY `playId` (F1 fix, play isolation plan 002) ─────────────
 * The SAME playbook can be open as the template (`playId: null`) or as any
 * number of Plays, each with its own snapshot ladder — `state.playbookId`
 * alone cannot distinguish them. A response for Play A's ladder that lands
 * after the rep has navigated to the template, or to Play B of the SAME
 * playbook, must not adopt onto whatever is open now, for exactly the reason
 * the `playbookId` check above exists. `playId` defaults to `null` (the
 * template) so the reorder success path below — the pre-existing caller that
 * never named one — keeps its original, template-only behavior unchanged.
 */
export function adoptSavedPlaybookDraft(
  playbookId: string,
  head: PlaybookDraftHead,
  playId: string | null = null,
): void {
  const state = usePlaybookDraftStore.getState();
  if (state.playbookId !== playbookId || state.playId !== playId) return;
  const oldDraft = state.draft;
  const oldRungs = oldDraft?.rungs ?? [];
  // ── STALE-draftKEY REDIRECT (Bug 1 fix, Codex adversarial review,
  // 2026-09-02) — see `playbook-draft.ts#PlaybookDraft.keyRedirects`'s doc
  // comment for the full race this closes. Every id-less rung this save just
  // minted a real id for gets an entry recording OLD key -> NEW key, merged
  // onto whatever this draft already carried (so a rung renamed more than
  // once still resolves through the whole chain via `resolveDraftKey`).
  const keyRedirects = { ...(oldDraft?.keyRedirects ?? {}) };
  oldRungs.forEach((oldRung, index) => {
    if (oldRung.id !== undefined) return;
    const persisted = head.rungs[index];
    if (!persisted) return;
    migrateCopyDraftScope(oldRung.draftKey, rungDraftKey(persisted.id));
    keyRedirects[oldRung.draftKey] = rungDraftKey(persisted.id);
  });
  usePlaybookDraftStore.setState({
    playbookId,
    draft: { ...seedDraftFromHead(head), keyRedirects },
  });
}

export function selectPlaybookDraft(state: PlaybookDraftState): PlaybookDraft | null {
  return state.draft;
}

const NO_RUNGS: readonly DraftRung[] = [];

/**
 * The ladder, or `[]` when nothing is seeded yet.
 *
 * Returns a stable module-level empty array rather than a fresh `[]`, so a
 * `useStore(selectDraftRungs)` subscription does not re-render on every
 * unrelated store write while the draft is still loading.
 */
export function selectDraftRungs(state: PlaybookDraftState): readonly DraftRung[] {
  return state.draft?.rungs ?? NO_RUNGS;
}

// Write-through persistence (2026-09-02 rip-out, U4)

/**
 * F1 fix (play isolation) — also guarded by `playId`, for the same reason
 * {@link adoptSavedPlaybookDraft} is: the rep may have navigated to the
 * template or a different Play of the SAME playbook while this op's write
 * was in flight, and this optimistic-apply / rollback must never land on
 * whatever draft happens to be open now instead of the one this op was
 * actually queued for.
 */
function setDraftIfCurrent(playbookId: string, playId: string | null, draft: PlaybookDraft): void {
  usePlaybookDraftStore.setState((state) =>
    state.playbookId === playbookId && state.playId === playId ? { draft } : state,
  );
}

/**
 * Plan 002, U6 (Key Technical Decision 11) — a play's ladder write never
 * creates plays. Checked before the optimistic apply so a play-mode caller
 * passing campaign ids fails before any network call (Review's Apply calls
 * `createPlay` directly in a play).
 */
function assertNoCampaignApplyInPlay(playId: string | null, applyToCampaignSfdcIds: string[] | undefined): void {
  if (playId !== null && applyToCampaignSfdcIds !== undefined && applyToCampaignSfdcIds.length > 0) {
    throw new Error('Apply to campaigns cannot run through a play ladder save.');
  }
}

async function writeLadder(
  apiFetch: ApiFetch,
  playbookId: string,
  playId: string | null,
  draft: PlaybookDraft,
  applyToCampaignSfdcIds: string[] | undefined,
): Promise<PlaybooksSavePlaybookRungsResponse> {
  if (draft.baseVersionId === null) {
    throw new Error('This Playbook has no saved version yet — reload and try again.');
  }
  const assembled = assembleDraftRungs(draft);
  if (playId !== null) {
    assertNoCampaignApplyInPlay(playId, applyToCampaignSfdcIds);
    // Plan 002, U6 (Decisions 6, 16) — the play's own ladder. The server never
    // forks on a ladder write: a Library id the play already has a copy of is
    // reuse-remapped onto the copy, another play's copy is refused (plan
    // 2026-09-27-006, Decision 2); the response carries the rungs as written.
    // Adapting to the playbook-save shape lets every caller and
    // `adoptSavedPlaybookDraft` stay shape-agnostic.
    const saved = await savePlayLadder(apiFetch, playId, assembled, draft.baseVersionId);
    return { id: playbookId, versionId: saved.versionId, versionNumber: null, rungs: saved.rungs, createdPlayIds: [] };
  }
  return applyToCampaignSfdcIds && applyToCampaignSfdcIds.length > 0
    ? saveAndApplyToCampaigns(apiFetch, playbookId, assembled, draft.baseVersionId, applyToCampaignSfdcIds)
    : savePlaybook(apiFetch, playbookId, assembled, draft.baseVersionId);
}

/** Per-playbook FIFO queue so two quick clicks cannot race each other's `expectedVersionId`. */
const persistQueues = new Map<string, Promise<unknown>>();

/**
 * Runs `task` after every previously-enqueued task for `playbookId` has
 * settled (success or failure), and makes `task` itself the new tail of that
 * same queue.
 *
 * Exported (Bug 3 fix, Codex adversarial review, 2026-09-02) so
 * `QualifyStep.tsx#WaterfallCard`'s waterfall-reorder write can share this
 * EXACT queue rather than running under its own, uncoordinated `saving`
 * boolean. Before this fix a drag-reorder (a sibling `/reorder` endpoint call)
 * had no coordination with every other ladder mutation's `persistLadder`
 * queue, so a reorder and a concurrent add/remove/mapping-change on the SAME
 * playbook could both be in flight against the server at once. Routing
 * `reorderWaterfall`'s call through this same `runExclusive` makes reorder and
 * every write-through save one true FIFO queue per playbook — the reorder
 * endpoint/request shape itself is unchanged, only its concurrency gating.
 */
export function runExclusive<T>(playbookId: string, task: () => Promise<T>): Promise<T> {
  const prior = persistQueues.get(playbookId) ?? Promise.resolve();
  const run = prior.then(task, task);
  // Chain the NEXT enqueued op off this one settling, regardless of outcome —
  // a failed op must not poison the queue for the ops after it.
  persistQueues.set(
    playbookId,
    run.then(
      () => undefined,
      () => undefined,
    ),
  );
  return run;
}

async function persistLadderOnce(
  apiFetch: ApiFetch,
  playbookId: string,
  /**
   * F1 fix — captured by the caller (`persistLadder`/`persistLadderInsideExclusive`)
   * at ENQUEUE time, never re-read from the store in here. See this
   * function's own no-op guard below and {@link persistLadder}'s comment for
   * the race this closes: a queued op must keep targeting the Play (or
   * template doorway) that was open when it was REQUESTED, not whatever is
   * open by the time its turn in the per-playbook FIFO queue arrives.
   */
  playId: string | null,
  op: (draft: PlaybookDraft) => PlaybookDraft,
  applyToCampaignSfdcIds: string[] | undefined,
  validateFreshHead?: (freshHead: PlaybookDraftHead) => void,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  const state = usePlaybookDraftStore.getState();
  // Mirrors the old `editDraft`'s no-op guard: a click that lands before the
  // head query resolves has no ladder to edit. F1 fix — ALSO compares
  // `state.playId` against the CAPTURED `playId` parameter above (never
  // `state.playId` read fresh): the rep may have navigated to the template
  // or to a different Play of the SAME playbook while this op sat queued
  // behind an earlier one on this same playbook. Without this check the op
  // below would apply against, and write onto, whatever draft is open NOW —
  // the template's `savePlaybook`, or another Play's `savePlayLadder`.
  if (state.playbookId !== playbookId || state.playId !== playId || state.draft === null) return null;
  assertNoCampaignApplyInPlay(playId, applyToCampaignSfdcIds);

  const before = state.draft;
  const applied = op(before);
  // Codex finding 3 fix — a true no-op edit (e.g. `remapDraftRungSequence`
  // against an unmapped rung, or one already pointing at the target sequence)
  // now comes back from `op` as the SAME `PlaybookDraft` reference
  // (`playbook-draft.ts#mapDraftRung`'s reference-equality short-circuit).
  // Skip the optimistic apply and the network write entirely rather than
  // posting a save that would write back exactly what the server already
  // has — same "nothing to persist" shape as the `state.draft === null`
  // guard above.
  //
  // NOT when `applyToCampaignSfdcIds` is non-empty: Review's "Apply to
  // campaigns" leg deliberately passes the identity op `(draft) => draft`
  // (no ladder change at all) specifically to persist ONLY the campaign
  // application on this same call — that write must still go through even
  // though `applied === before` by construction for that caller.
  const hasCampaignSideEffect = applyToCampaignSfdcIds !== undefined && applyToCampaignSfdcIds.length > 0;
  if (applied === before && !hasCampaignSideEffect) return null;
  // Optimistic: the applied ladder renders immediately (e.g. a just-added rung
  // appears in the rail right away) while the POST is in flight — the same
  // optimistic-then-reconcile shape `WaterfallCard`'s reorder already uses.
  setDraftIfCurrent(playbookId, playId, applied);

  try {
    const response = await writeLadder(apiFetch, playbookId, playId, applied, applyToCampaignSfdcIds);
    adoptSavedPlaybookDraft(playbookId, { versionId: response.versionId, rungs: response.rungs }, playId);
    return response;
  } catch (error) {
    if (!isVersionConflictError(error)) {
      setDraftIfCurrent(playbookId, playId, before);
      throw error;
    }

    // The head moved under this ladder since `baseVersionId` was read.
    // Refetch it, re-anchor a fresh draft onto it, and re-apply the SAME op —
    // each op ("add segmentation X", "remove rung id Y", "set mapping on rung
    // id Z") is written to be re-appliable against any current ladder, which
    // is what makes a single blind retry correct instead of a guess.
    let freshHead: PlaybookDraftHead;
    try {
      // Plan 002, U6 — in a play the fresh head is the play's ladder (its pin).
      const fresh = playId === null ? await getPlaybook(apiFetch, playbookId) : await getPlaybook(apiFetch, playbookId, { playId });
      if (!fresh.head) throw error;
      freshHead = fresh.head;
    } catch {
      setDraftIfCurrent(playbookId, playId, before);
      throw error;
    }

    // ── Bug 2 fix (Codex adversarial review, 2026-09-02) — an Apply-to-
    // campaigns retry must not silently apply a ladder the rep never
    // reviewed. `ReviewStep` passes `validateFreshHead` (built from
    // `review-gate.ts#ladderMatchesReviewedRungs`) so this refetched head is
    // checked against what was actually approved BEFORE the retry writes it.
    // A mismatch adopts the fresh head locally (so the rep sees what actually
    // changed) and rejects with a message the caller's own `.catch` surfaces
    // as a toast, instead of proceeding to `writeLadder` with an unreviewed
    // ladder. Every other caller (add/remove/mapping/sender) passes nothing
    // here and is unaffected.
    if (validateFreshHead) {
      try {
        validateFreshHead(freshHead);
      } catch (validationError) {
        setDraftIfCurrent(playbookId, playId, rebaseDraft(freshHead));
        throw validationError;
      }
    }

    const rebasedFreshDraft = rebaseDraft(freshHead);
    const retryDraft = op(rebasedFreshDraft);
    setDraftIfCurrent(playbookId, playId, retryDraft);
    // ── RETRY NO-OP SHORT-CIRCUIT (round-3 adversarial review, distinct from
    // the "Bug 2" `validateFreshHead` fix above despite the similar name) —
    // the same reference-equality no-op short-circuit as the first-attempt
    // guard above, applied here too. A 409 is SPECIFICALLY triggered by a
    // concurrent change to this same playbook, which makes a no-op on the
    // retry MORE likely than on the first attempt, not less: the rung this
    // op targets may have been concurrently deleted, or already carries the
    // value this op would set. Before this fix, that case still posted a real
    // write-through save that would write back exactly what the fresh head
    // already has. Unlike the first-attempt guard (which leaves `before`
    // untouched because nothing about it changed), the local draft here still
    // needs to adopt `retryDraft` — it IS `rebasedFreshDraft` in the no-op
    // case, i.e. the fresh state as of the refetch — so `setDraftIfCurrent`
    // above runs unconditionally and this check only decides whether to skip
    // the second network write. `hasCampaignSideEffect` carries the same
    // "Apply to campaigns" carve-out as the first-attempt check: that call
    // passes the identity op specifically to persist the campaign
    // application on this exact write, so it must always go through even
    // when `retryDraft === rebasedFreshDraft` by construction.
    if (retryDraft === rebasedFreshDraft && !hasCampaignSideEffect) {
      return null;
    }
    try {
      const response = await writeLadder(apiFetch, playbookId, playId, retryDraft, applyToCampaignSfdcIds);
      adoptSavedPlaybookDraft(playbookId, { versionId: response.versionId, rungs: response.rungs }, playId);
      return response;
    } catch (retryError) {
      // A second failure is NOT retried again — roll back to the confirmed
      // fresh head (never leave a phantom local edit the server never
      // accepted) and let the caller's own catch show it as a toast.
      setDraftIfCurrent(playbookId, playId, rebaseDraft(freshHead));
      throw retryError;
    }
  }
}

/**
 * Applies `op` to the ladder open for `playbookId` and persists the result
 * immediately via `POST /v2/playbooks/:id/save` — the write-through mechanism
 * every ladder-mutating action routes through as of the 2026-09-02 rip-out.
 * Returns the server's response (server-minted ids, new `expectedVersionId`)
 * once the store has adopted it, or `null` if nothing is seeded for
 * `playbookId` to apply `op` to.
 *
 * `applyToCampaignSfdcIds`, when non-empty, is Review's "Apply to campaigns"
 * leg on this SAME call: it is fed the identity op `(draft) => draft` (no
 * ladder change, just "persist what's already here plus these campaign ids"),
 * which is what lets Apply share this exact 409-retry mechanism instead of
 * duplicating it.
 *
 * See this module's header and `playbook-draft.ts`'s header for the full
 * write-through design; the 409 retry-once-then-toast behavior is documented
 * on the internal `persistLadderOnce` this delegates to.
 *
 * F1 fix (play isolation, plan 002) — reads `usePlaybookDraftStore.getState().playId`
 * SYNCHRONOUSLY, right here, before `runExclusive` even enqueues the task —
 * this function always runs in the same tick as the rep's click (no `await`
 * before it), so this is genuinely "whichever Play, or the template doorway,
 * is open at the moment this edit was requested". `persistLadderOnce` trusts
 * this captured value for the rest of the op's life instead of re-reading
 * `state.playId` once the op finally reaches the front of the per-playbook
 * FIFO queue, which is what makes a navigation that happens while this op is
 * still queued unable to redirect it onto a different Play's (or the
 * template's) ladder.
 */
export function persistLadder(
  apiFetch: ApiFetch,
  playbookId: string,
  op: (draft: PlaybookDraft) => PlaybookDraft,
  applyToCampaignSfdcIds?: string[],
  /**
   * Bug 2 fix — an optional check run against the FRESHLY refetched head on a
   * 409 retry, before that retry is allowed to write. Only `ReviewStep`'s
   * "Apply to campaigns" call passes one (see `persistLadderOnce`'s own doc
   * comment); every other ladder mutation leaves this `undefined` and keeps
   * today's behavior exactly.
   */
  validateFreshHead?: (freshHead: PlaybookDraftHead) => void,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  const playId = usePlaybookDraftStore.getState().playId;
  return runExclusive(playbookId, () =>
    persistLadderOnce(apiFetch, playbookId, playId, op, applyToCampaignSfdcIds, validateFreshHead),
  );
}

/**
 * Plan 2026-09-25-002, U6 (Key Technical Decision 16) — {@link persistLadder}
 * for a caller that ALREADY holds `runExclusive(playbookId, …)`:
 * `QualifyStep#WaterfallCard.handleReorder` in a play, which keeps its cache
 * patch and matched-members invalidation inside the same exclusive task (see
 * that handler's doc). Calling {@link persistLadder} there would enqueue
 * behind the very task awaiting it and never run. Same no-op short-circuit,
 * 409 retry and adoption as {@link persistLadder}; never carries campaign ids.
 *
 * F1 fix — `playId` is taken as an explicit parameter rather than read from
 * the store: unlike {@link persistLadder}, this function is called from
 * INSIDE an already-executing `runExclusive` task, which can start running
 * well after the rep's click (once every earlier op on this playbook has
 * settled) — reading the store fresh at that point would reintroduce the
 * exact staleness bug this fix closes. The one caller
 * (`QualifyStep#WaterfallCard.handleReorder`) already closes over the
 * correct value (its own `playId` prop, captured at the moment the drag
 * ended) and passes it straight through.
 */
export function persistLadderInsideExclusive(
  apiFetch: ApiFetch,
  playbookId: string,
  playId: string | null,
  op: (draft: PlaybookDraft) => PlaybookDraft,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  return persistLadderOnce(apiFetch, playbookId, playId, op, undefined);
}

/** Write-through "Add segmentation" — stages then immediately persists a new rung. */
export function addPlaybookDraftRung(
  apiFetch: ApiFetch,
  playbookId: string,
  input: NewDraftRungInput,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  return persistLadder(apiFetch, playbookId, (draft) => addDraftRung(draft, input));
}

/** Write-through "Remove group". */
export function removePlaybookDraftRung(
  apiFetch: ApiFetch,
  playbookId: string,
  draftKey: string,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  return persistLadder(apiFetch, playbookId, (draft) => removeDraftRung(draft, draftKey));
}

/** Write-through sequence-mapping change (`SequenceMappingDrawer`'s Save). */
export function setPlaybookDraftRungMapping(
  apiFetch: ApiFetch,
  playbookId: string,
  draftKey: string,
  mapping: SequenceMapping | null,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  return persistLadder(apiFetch, playbookId, (draft) => setDraftRungMapping(draft, draftKey, mapping));
}

// U9 (SN1) / Decision 3 — `setPlaybookDraftRungSender` was here and is
// DELETED with `setDraftRungSender` and their one caller. See
// `playbook-draft.ts`'s note at the same spot.

/**
 * Write-through sequence remap on an existing rung — `SequencesStep.tsx`'s
 * "Save as new copy" flow, once the duplicate exists, switching this rung
 * over to it.
 *
 * Delegates to {@link remapDraftRungSequence}, which carries ONLY the new
 * `sequenceId` (see that function's doc comment for why this is what makes a
 * 409 retry re-read the rung's `sender`/`cc`/`replyTo`/`bookingLinkIdentity`
 * fresh instead of clobbering a concurrent edit to them).
 */
export function remapPlaybookDraftRungSequence(
  apiFetch: ApiFetch,
  playbookId: string,
  draftKey: string,
  newSequenceId: string,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  return persistLadder(apiFetch, playbookId, (draft) => remapDraftRungSequence(draft, draftKey, newSequenceId));
}

/**
 * Playbooks QA round 4, U6 (S2) / Decision 13 — write-through SEGMENTATION
 * remap on one rung: the usage sidebar's "Save a new version" for a shared
 * segmentation, once the fork exists, switching ONLY the origin rung over to
 * it.
 *
 * Delegates to {@link remapDraftRungSegmentation}, which carries ONLY the new
 * `segmentationId` — that is what makes a 409 retry re-read the rung's
 * `mapping` (sequence/sender/cc) off the FRESH head instead of clobbering a
 * concurrent edit with a pre-conflict snapshot, exactly as its sequence
 * sibling above does.
 */
export function remapPlaybookDraftRungSegmentation(
  apiFetch: ApiFetch,
  playbookId: string,
  draftKey: string,
  newSegmentationId: string,
): Promise<PlaybooksSavePlaybookRungsResponse | null> {
  return persistLadder(apiFetch, playbookId, (draft) =>
    remapDraftRungSegmentation(draft, draftKey, newSegmentationId),
  );
}

/**
 * Round-4-adversarial-review fix (Bug 4) — disambiguates a `persistLadder`-
 * family `null` return. `persistLadderOnce` has THREE distinct paths that
 * return `null`: (1) nothing seeded for this playbook at all (a genuine
 * failure — there is no draft to have applied anything to), (2) the
 * first-attempt op was already a no-op (`applied === before`), and (3) a 409
 * retry discovered the FRESHLY refetched head already carries the target
 * state (`retryDraft === rebasedFreshDraft`) — e.g. a concurrent actor
 * already completed the exact same change. Cases (2) and (3) are SUCCESSES —
 * the local draft already reflects (or was just set to reflect) the correct
 * state by the time this resolves — but a caller that only checks "is the
 * response non-null" cannot tell them apart from case (1), which is exactly
 * what let a genuinely successful concurrent remap get reported to the rep as
 * a failure (`SequencesStep.tsx`'s `handleSaveAsNewCopy`).
 *
 * Reads the CURRENT live draft directly rather than trusting the response:
 * both no-op paths above leave the draft (for THIS playbook) already holding
 * the rung's correct mapping by the time `persistLadder` resolves, so this is
 * a reliable way to tell a genuine failure (case 1 — nothing seeded, or the
 * rung doesn't exist / doesn't map here) from a no-op success (cases 2/3).
 * `resolveDraftKey` follows the same stale-`draftKey` redirect chain every
 * other draft lookup does, since the caller's `draftKey` may have been
 * renamed since it was captured.
 *
 * Round-2-fixes M2 widening — `sequenceId` also accepts `null`, which asks
 * "is this rung UNMAPPED now?" instead of "does this rung map sequence X
 * now?". This is the unmap handler's own version of the same disambiguation:
 * `SequencesStep.tsx#handleRemoveMapping` and
 * `SequenceMappingDrawer.tsx#confirmSwap` both call `setPlaybookDraftRungMapping`
 * with a `null`/new mapping and need to tell "the write never landed" apart
 * from "a 409 retry discovered the fresh head already carries the mapping I
 * was trying to set" before running their success cleanup (dropping the OLD
 * sequence's copy drafts and staged Generate-with-AI review) — see
 * `setDraftRungMapping`'s own doc comment (`playbook-draft.ts`) for the
 * matching reference-equality no-op this widening reads back out of.
 */
export function draftRungMapsToSequence(
  playbookId: string,
  draftKey: string,
  sequenceId: string | null,
): boolean {
  const state = usePlaybookDraftStore.getState();
  if (state.playbookId !== playbookId || state.draft === null) return false;
  const resolvedKey = resolveDraftKey(state.draft, draftKey);
  const rung = state.draft.rungs.find((r) => r.draftKey === resolvedKey);
  if (!rung) return false;
  return (rung.mapping?.sequenceId ?? null) === sequenceId;
}

/**
 * F2 fix — the rung's CURRENT `mapping.sequenceId` (`null` if unmapped), or
 * `undefined` if nothing is seeded for `playbookId` or the rung cannot be
 * found (the genuine-failure case {@link draftRungMapsToSequence} already
 * disambiguates for one target id).
 *
 * A play-mode ladder save can FORK the sequence a rung is being mapped to
 * INTO A NEW PLAY-OWNED COPY, so the rung's adopted `mapping.sequenceId` can
 * end up being neither the OLD sequence the caller is swapping away from nor
 * the exact id the caller asked for (the Library's pre-fork id) — it is the
 * server's rewritten (forked) id. `draftRungMapsToSequence(…, selectedId)`
 * cannot see that: it only answers "does this rung map EXACTLY this id",
 * which reads `false` for a legitimately successful forked swap, and a
 * caller using that as its "did the swap land" signal skips its own success
 * cleanup (`SequenceMappingDrawer.tsx#confirmSwap` is the confirmed case —
 * see its own call site's comment). Reading the sequenceId directly and
 * comparing it against the FORK SOURCE (the old sequence id) — "is this rung
 * no longer pointing at what it used to point at" — is fork-agnostic and
 * correct whether or not this rung's target was rewritten.
 */
export function draftRungMappingSequenceId(playbookId: string, draftKey: string): string | null | undefined {
  const state = usePlaybookDraftStore.getState();
  if (state.playbookId !== playbookId || state.draft === null) return undefined;
  const resolvedKey = resolveDraftKey(state.draft, draftKey);
  const rung = state.draft.rungs.find((r) => r.draftKey === resolvedKey);
  if (!rung) return undefined;
  return rung.mapping?.sequenceId ?? null;
}

/**
 * Playbooks QA round 4, Codex round-2 review (wave 2-3, finding 2) —
 * `draftRungMapsToSequence`'s segmentation twin. `remapPlaybookDraftRungSegmentation`
 * can resolve with a `null` `persistLadder` response (the rung/draft/playbook
 * moved out from under the request) while the segmentation FORK itself still
 * committed on the server — the caller needs this to tell "the fork landed
 * on this rung" apart from "the fork exists but nothing switched to it" before
 * reporting success, exactly as the sequence save-as-new-copy flow already does.
 */
export function draftRungMapsToSegmentation(
  playbookId: string,
  draftKey: string,
  segmentationId: string,
): boolean {
  const state = usePlaybookDraftStore.getState();
  if (state.playbookId !== playbookId || state.draft === null) return false;
  const resolvedKey = resolveDraftKey(state.draft, draftKey);
  const rung = state.draft.rungs.find((r) => r.draftKey === resolvedKey);
  if (!rung) return false;
  return rung.segmentationId === segmentationId;
}

